export interface TabDef<T extends string = string> {
  id: T;
  label: string;
}

export const TAB_QUERY_KEY = 'tab';

const toParams = (search: string | URLSearchParams): URLSearchParams =>
  typeof search === 'string' ? new URLSearchParams(search) : new URLSearchParams(search.toString());

// Unknown or unauthorised values fall back to the default rather than erroring.
export const resolveTabIn = <T extends string>(
  tabs: TabDef<T>[],
  defaultTab: T,
  search: string | URLSearchParams
): T => {
  const raw = toParams(search).get(TAB_QUERY_KEY);
  const hit = tabs.find((t) => t.id === raw);
  return hit ? hit.id : defaultTab;
};

// Keeps every other query param; the default tab is written as no param.
export const searchWithTabFor = <T extends string>(
  defaultTab: T,
  search: string | URLSearchParams,
  tab: T
): string => {
  const params = toParams(search);
  if (tab === defaultTab) params.delete(TAB_QUERY_KEY);
  else params.set(TAB_QUERY_KEY, tab);
  const out = params.toString();
  return out ? `?${out}` : '';
};

// Roving-tabindex keyboard model: arrows wrap, Home/End jump. Null = not a nav key.
export const tabForKey = <T extends string>(
  tabs: TabDef<T>[],
  current: T,
  key: string
): T | null => {
  if (!tabs.length) return null;
  const idx = Math.max(
    0,
    tabs.findIndex((t) => t.id === current)
  );
  switch (key) {
    case 'ArrowRight':
      return tabs[(idx + 1) % tabs.length].id;
    case 'ArrowLeft':
      return tabs[(idx - 1 + tabs.length) % tabs.length].id;
    case 'Home':
      return tabs[0].id;
    case 'End':
      return tabs[tabs.length - 1].id;
    default:
      return null;
  }
};
